import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, FileText, X, CheckCircle, AlertCircle, Loader2, FileUp, Code, Palette, MessageSquare, ChevronRight, ChevronLeft, BookOpen, HelpCircle, CheckSquare, Calculator, Trash2, RefreshCw, ListChecks, Tag, Building2, Award } from 'lucide-react';
import { supabase, getSupabaseUrl } from '../../supabase/client';
import { parsePaperFilename, ParsedPaperFilename, getParsedInfoText, buildLevelName } from '../../utils/paperFilenameParser';
import { categoryStore, Organization as StoreOrganization, CourseCategory as StoreCourseCategory, Level } from '../../store/categoryStore';

type CourseCategoryType = 'scratch' | 'python' | 'cpp' | 'other';
type QuestionType = 'single_choice' | 'multi_choice' | 'judge' | 'programming';

interface CourseCategoryConfig {
  type: CourseCategoryType;
  name: string;
  programmingName: string;
  icon: React.ElementType;
  color: string;
  bgColor: string;
}

// 课程分类配置：从 categoryStore 动态获取名称，与【类目与定价】栏目实时同步
const getCourseCategoryConfigs = (): CourseCategoryConfig[] => {
  const categories = categoryStore.getCategories();
  const iconMap: Record<string, React.ElementType> = { 'scratch': Palette, 'python': Code, 'cpp': Code, 'other': MessageSquare };
  const colorMap: Record<string, string> = { 'scratch': 'text-purple-600', 'python': 'text-blue-600', 'cpp': 'text-indigo-600', 'other': 'text-green-600' };
  const bgColorMap: Record<string, string> = { 'scratch': 'bg-purple-500', 'python': 'bg-blue-500', 'cpp': 'bg-indigo-500', 'other': 'bg-green-500' };
  return categories.map(cat => ({
    type: cat.code || cat.id,
    name: cat.name,
    programmingName: `${cat.name}题`,
    icon: iconMap[cat.code] || Code,
    color: colorMap[cat.code] || 'text-gray-600',
    bgColor: bgColorMap[cat.code] || 'bg-gray-500',
  }));
};
const courseCategoryConfigs = getCourseCategoryConfigs();

interface QuestionTypeConfig {
  type: QuestionType;
  name: string;
  icon: React.ElementType;
  isObjective: boolean;
}

const questionTypes: QuestionTypeConfig[] = [
  { type: 'single_choice', name: '单选题', icon: HelpCircle, isObjective: true },
  { type: 'multi_choice', name: '多选题', icon: CheckSquare, isObjective: true },
  { type: 'judge', name: '判断题', icon: Calculator, isObjective: true },
  { type: 'programming', name: '编程题', icon: Code, isObjective: false },
];

interface QuestionConfigItem {
  count: number;
  hasCorrectAnswer: boolean;
  hasExplanation: boolean;
  hasReferenceAnswer: boolean;
  hasScoringPoints: boolean;
}

interface FileItem {
  id: string;
  file: File;
  name: string;
  size: number;
  status: 'pending' | 'reading' | 'uploading' | 'parsing' | 'success' | 'error';
  progress: number;
  message: string;
  questionCount?: number;
  pendingPaperId?: string;
  parsedInfo?: ParsedPaperFilename;
}

const MAX_FILES = 30;
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ALLOWED_EXTENSIONS = ['docx', 'pdf'];

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const generateId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

const inferCourseCategoryFromName = (name: string): CourseCategoryType => {
  if (name.includes('图形化')) return 'scratch';
  if (name.includes('Python')) return 'python';
  if (name.includes('C++')) return 'cpp';
  return 'other';
};

export default function PaperUpload() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [courseCategory, setCourseCategory] = useState<CourseCategoryType>('scratch');
  const [questionConfig, setQuestionConfig] = useState<Record<QuestionType, QuestionConfigItem>>({
    single_choice: { count: 0, hasCorrectAnswer: true, hasExplanation: true, hasReferenceAnswer: false, hasScoringPoints: false },
    multi_choice: { count: 0, hasCorrectAnswer: true, hasExplanation: true, hasReferenceAnswer: false, hasScoringPoints: false },
    judge: { count: 0, hasCorrectAnswer: true, hasExplanation: true, hasReferenceAnswer: false, hasScoringPoints: false },
    programming: { count: 0, hasCorrectAnswer: false, hasExplanation: false, hasReferenceAnswer: true, hasScoringPoints: true },
  });
  const [files, setFiles] = useState<FileItem[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  const [organizations, setOrganizations] = useState<StoreOrganization[]>([]);
  const [selectedOrganization, setSelectedOrganization] = useState<string>('');
  const [categories, setCategories] = useState<StoreCourseCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [levels, setLevels] = useState<Level[]>([]);
  const [selectedLevel, setSelectedLevel] = useState<string>('');
  const [loadingData, setLoadingData] = useState(false);

  const selectedCategoryName = selectedCategory ? categories.find(c => c.id === selectedCategory)?.name || '' : '';
  const inferredCourseCategory = selectedCategoryName ? inferCourseCategoryFromName(selectedCategoryName) : 'other';
  const inferredConfig = courseCategoryConfigs.find(c => c.type === inferredCourseCategory)!;

  useEffect(() => {
    const loadData = async () => {
      setLoadingData(true);
      try {
        await categoryStore.init();
        setOrganizations(categoryStore.getOrganizations());
        setCategories(categoryStore.getCategories());
        setLevels(categoryStore.getLevels());
      } catch (err) {
        
      } finally {
        setLoadingData(false);
      }
    };
    loadData();

    const unsubscribe = categoryStore.subscribe(() => {
      setOrganizations(categoryStore.getOrganizations());
      setCategories(categoryStore.getCategories());
      setLevels(categoryStore.getLevels());
    });

    return () => unsubscribe();
  }, []);

  const filteredCategories = useMemo(() => {
    if (!selectedOrganization) return [];
    return categories.filter(c => c.orgId === selectedOrganization);
  }, [categories, selectedOrganization]);

  const filteredLevels = useMemo(() => {
    if (!selectedCategory) return [];
    return levels.filter(l => l.catId === selectedCategory);
  }, [levels, selectedCategory]);

  useEffect(() => {
    if (selectedOrganization) {
      const cats = categories.filter(c => c.orgId === selectedOrganization);
      if (cats.length > 0 && !cats.find(c => c.id === selectedCategory)) {
        setSelectedCategory('');
        setSelectedLevel('');
      }
    }
  }, [selectedOrganization, categories, selectedCategory]);

  useEffect(() => {
    if (selectedCategory) {
      const levs = levels.filter(l => l.catId === selectedCategory);
      if (levs.length > 0 && !levs.find(l => l.id === selectedLevel)) {
        setSelectedLevel('');
      }
    }
  }, [selectedCategory, levels, selectedLevel]);

  useEffect(() => {
    if (selectedLevel) {
      const level = levels.find(l => l.id === selectedLevel);
      if (level) {
        setQuestionConfig(prev => ({
          ...prev,
          single_choice: { ...prev.single_choice, count: level.singleChoiceCount || 0 },
          multi_choice: { ...prev.multi_choice, count: level.multiChoiceCount || 0 },
          judge: { ...prev.judge, count: level.judgeCount || 0 },
          programming: { ...prev.programming, count: level.programmingCount || 0 },
        }));
      }
    }
  }, [selectedLevel, levels]);

  const totalQuestions = useMemo(() => {
    return Object.values(questionConfig).reduce((sum, item) => sum + item.count, 0);
  }, [questionConfig]);

  const remainingSlots = MAX_FILES - files.length;

  const stats = useMemo(() => {
    const pending = files.filter(f => f.status === 'pending').length;
    const processing = files.filter(f => ['reading', 'uploading', 'parsing'].includes(f.status)).length;
    const success = files.filter(f => f.status === 'success').length;
    const error = files.filter(f => f.status === 'error').length;
    const done = success + error;
    return { pending, processing, success, error, done, total: files.length };
  }, [files]);

  const allDone = stats.total > 0 && stats.done === stats.total;

  const updateQuestionConfig = (type: QuestionType, field: keyof QuestionConfigItem, value: number | boolean) => {
    setQuestionConfig(prev => ({
      ...prev,
      [type]: { ...prev[type], [field]: value },
    }));
  };

  const handleNext = () => {
    if (currentStep < 2) setCurrentStep(prev => prev + 1);
  };

  const handlePrev = () => {
    if (currentStep > 1) setCurrentStep(prev => prev - 1);
  };

  const validateAndAddFiles = (newFiles: File[]) => {
    const validFiles: FileItem[] = [];
    const errors: string[] = [];

    for (const f of newFiles) {
      if (files.length + validFiles.length >= MAX_FILES) {
        errors.push(`最多选择 ${MAX_FILES} 个文件`);
        break;
      }
      const ext = f.name.split('.').pop()?.toLowerCase();
      if (!ALLOWED_EXTENSIONS.includes(ext || '')) {
        errors.push(`${f.name}: 仅支持 .docx 和 .pdf 格式`);
        continue;
      }
      if (f.size > MAX_FILE_SIZE) {
        errors.push(`${f.name}: 文件大小超过10MB限制`);
        continue;
      }
      if (files.some(existing => existing.name === f.name && existing.size === f.size)) {
        errors.push(`${f.name}: 文件已添加`);
        continue;
      }
      const parsedInfo = parsePaperFilename(f.name);

      validFiles.push({
        id: generateId(),
        file: f,
        name: f.name,
        size: f.size,
        status: 'pending',
        progress: 0,
        message: '',
        parsedInfo,
      });
    }

    if (errors.length > 0) {
      alert(errors.join('\n'));
    }

    if (validFiles.length > 0) {
      setFiles(prev => [...prev, ...validFiles]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      validateAndAddFiles(Array.from(e.target.files));
      e.target.value = '';
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      validateAndAddFiles(Array.from(e.dataTransfer.files));
    }
  }, [files]);

  const removeFile = (id: string) => {
    setFiles(prev => prev.filter(f => f.id !== id));
  };

  const clearAllFiles = () => {
    setFiles([]);
  };

  const updateFileItem = (id: string, updates: Partial<FileItem>) => {
    setFiles(prev => prev.map(f => f.id === id ? { ...f, ...updates } : f));
  };

  const processSingleFile = async (id: string) => {
    try {
      updateFileItem(id, { status: 'reading', message: '正在读取文件...', progress: 10 });

      const currentFile = files.find(f => f.id === id);
      if (!currentFile) throw new Error('文件不存在');

      const base64Content = await fileToBase64(currentFile.file);
      updateFileItem(id, { status: 'uploading', message: '正在上传...', progress: 20 });

      const { data: { session } } = await supabase.auth.getSession();

      updateFileItem(id, { status: 'parsing', message: '正在解析试卷内容...', progress: 50 });

      const selectedOrgName = organizations.find(o => o.id === selectedOrganization)?.name || '';
      const selectedLevelName = selectedLevel ? levels.find(l => l.id === selectedLevel)?.name || '' : '';
      const selectedLevelId = selectedLevel || '';

      const response = await fetch(`${getSupabaseUrl()}/functions/v1/paper-processing`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: session ? `Bearer ${session.access_token}` : '',
        },
        body: JSON.stringify({
          fileContent: base64Content,
          fileType: currentFile.file.name.split('.').pop()?.toLowerCase(),
          fileName: currentFile.file.name,
          userId: session?.user?.id,
          courseCategory: inferredCourseCategory,
          questionConfig: {
            single_choice: { count: questionConfig.single_choice.count, score: 0 },
            multi_choice: { count: questionConfig.multi_choice.count, score: 0 },
            judge: { count: questionConfig.judge.count, score: 0 },
            programming: { count: questionConfig.programming.count, score: 0 },
          },
          extractConfig: {
            extractAnswer: true,
            extractExplanation: true,
            extractScoringPoints: true,
          },
          parsedInfo: {
            ...currentFile.parsedInfo,
            organization: selectedOrgName,
            categoryType: inferredCourseCategory,
            levelName: selectedLevelName || buildLevelName(inferredCourseCategory, currentFile.parsedInfo?.levelText),
            levelId: selectedLevelId,
          },
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || '解析失败');
      }

      const successMessage = result.levelMatchWarning
        ? `成功解析 ${result.questionCount} 道题目（⚠️ ${result.levelMatchWarning}）`
        : `成功解析 ${result.questionCount} 道题目`;

      updateFileItem(id, {
        status: 'success',
        message: successMessage,
        progress: 100,
        questionCount: result.questionCount,
        pendingPaperId: result.pendingPaperId,
      });
    } catch (error: any) {
      updateFileItem(id, {
        status: 'error',
        message: error.message || '处理失败',
        progress: 0,
      });
    }
  };

  const processAllFiles = async () => {
    const pendingIds = files.filter(f => f.status === 'pending' || f.status === 'error').map(f => f.id);
    if (pendingIds.length === 0) return;

    setIsProcessing(true);

    for (const id of pendingIds) {
      await processSingleFile(id);
    }

    setIsProcessing(false);
  };

  const retryFile = async (id: string) => {
    updateFileItem(id, { status: 'pending', message: '', progress: 0 });
    setIsProcessing(true);
    await processSingleFile(id);
    setIsProcessing(false);
  };

  const resetUpload = () => {
    setFiles([]);
    setIsProcessing(false);
    setCurrentStep(1);
    setQuestionConfig({
      single_choice: { count: 0, hasCorrectAnswer: true, hasExplanation: true, hasReferenceAnswer: false, hasScoringPoints: false },
      multi_choice: { count: 0, hasCorrectAnswer: true, hasExplanation: true, hasReferenceAnswer: false, hasScoringPoints: false },
      judge: { count: 0, hasCorrectAnswer: true, hasExplanation: true, hasReferenceAnswer: false, hasScoringPoints: false },
      programming: { count: 0, hasCorrectAnswer: false, hasExplanation: false, hasReferenceAnswer: true, hasScoringPoints: true },
    });
  };

  const renderStep1 = () => (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-4xl mx-auto">
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
            <BookOpen className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">步骤 1：配置试卷</h3>
            <p className="text-sm text-gray-500">选择考评体系、课程分类、级别，并设置题型数量</p>
          </div>
        </div>

        {/* 三级级联选择 */}
        <div className="mb-6 space-y-5">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">考评体系</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {loadingData ? (
                <div className="px-4 py-2 text-sm text-gray-400">加载中...</div>
              ) : organizations.length === 0 ? (
                <div className="px-4 py-2 text-sm text-gray-400">暂无考评体系</div>
              ) : (
                organizations.map(org => (
                  <motion.button
                    key={org.id}
                    onClick={() => { setSelectedOrganization(org.id); setSelectedCategory(''); setSelectedLevel(''); }}
                    className={`px-4 py-2 text-sm rounded-lg border-2 transition-all ${
                      selectedOrganization === org.id
                        ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {org.name}
                  </motion.button>
                ))
              )}
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-2">
              <BookOpen className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">课程分类</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {!selectedOrganization ? (
                <div className="px-4 py-2 text-sm text-gray-400">请先选择考评体系</div>
              ) : filteredCategories.length === 0 ? (
                <div className="px-4 py-2 text-sm text-gray-400">暂无课程分类</div>
              ) : (
                filteredCategories.map(cat => (
                  <motion.button
                    key={cat.id}
                    onClick={() => { setSelectedCategory(cat.id); setSelectedLevel(''); }}
                    className={`px-4 py-2 text-sm rounded-lg border-2 transition-all ${
                      selectedCategory === cat.id
                        ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {cat.name}
                  </motion.button>
                ))
              )}
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Award className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">级别分类</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {!selectedCategory ? (
                <div className="px-4 py-2 text-sm text-gray-400">请先选择课程分类</div>
              ) : filteredLevels.length === 0 ? (
                <div className="px-4 py-2 text-sm text-gray-400">暂无级别</div>
              ) : (
                filteredLevels.map(level => (
                  <motion.button
                    key={level.id}
                    onClick={() => setSelectedLevel(level.id)}
                    className={`px-4 py-2 text-sm rounded-lg border-2 transition-all ${
                      selectedLevel === level.id
                        ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {level.name}
                  </motion.button>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="mb-6 p-4 bg-blue-50 rounded-xl flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="text-sm text-gray-600">已配置题目</div>
            <div className="text-2xl font-bold text-blue-600">{totalQuestions} <span className="text-sm font-normal text-gray-500">道</span></div>
          </div>
          {selectedCategoryName && (
            <span className="text-xs text-gray-500">非客观题识别为：{inferredConfig.programmingName}</span>
          )}
        </div>

        <div className="space-y-3">
          {questionTypes.map((qt) => {
            const config = questionConfig[qt.type];
            const isProgramming = qt.type === 'programming';
            const displayName = isProgramming ? inferredConfig.programmingName : qt.name;
            const Icon = qt.icon;

            return (
              <div key={qt.type} className="border border-gray-200 rounded-xl p-4">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-3 flex-shrink-0" style={{ width: '160px' }}>
                    <div className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center">
                      <Icon className="w-4 h-4 text-gray-600" />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900 text-sm whitespace-nowrap">{displayName}</span>
                      <span className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full whitespace-nowrap">
                        {qt.isObjective ? '客观题' : '非客观题'}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0" style={{ width: '100px' }}>
                    <input
                      type="number"
                      min={0}
                      value={config.count}
                      onChange={(e) => updateQuestionConfig(qt.type, 'count', parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="数量"
                    />
                  </div>

                  <div className="flex items-center gap-4 flex-1">
                    {qt.isObjective ? (
                      <>
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={config.hasCorrectAnswer}
                            onChange={(e) => updateQuestionConfig(qt.type, 'hasCorrectAnswer', e.target.checked)}
                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">正确答案</span>
                        </label>
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={config.hasExplanation}
                            onChange={(e) => updateQuestionConfig(qt.type, 'hasExplanation', e.target.checked)}
                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">答案解析</span>
                        </label>
                      </>
                    ) : (
                      <>
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={config.hasReferenceAnswer}
                            onChange={(e) => updateQuestionConfig(qt.type, 'hasReferenceAnswer', e.target.checked)}
                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">参考答案</span>
                        </label>
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={config.hasScoringPoints}
                            onChange={(e) => updateQuestionConfig(qt.type, 'hasScoringPoints', e.target.checked)}
                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">评分标准</span>
                        </label>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-8 flex justify-end">
          <button
            onClick={handleNext}
            disabled={totalQuestions === 0}
            className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            下一步
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </motion.div>
  );

  const renderFileItem = (fileItem: FileItem) => {
    const isActive = ['reading', 'uploading', 'parsing'].includes(fileItem.status);
    const isDone = fileItem.status === 'success' || fileItem.status === 'error';

    return (
      <motion.div
        key={fileItem.id}
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, x: -20 }}
        className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
          fileItem.status === 'success' ? 'border-green-200 bg-green-50' :
          fileItem.status === 'error' ? 'border-red-200 bg-red-50' :
          isActive ? 'border-blue-200 bg-blue-50' :
          'border-gray-200 bg-white'
        }`}
      >
        <div className="flex-shrink-0">
          {fileItem.status === 'success' ? (
            <CheckCircle className="w-5 h-5 text-green-600" />
          ) : fileItem.status === 'error' ? (
            <AlertCircle className="w-5 h-5 text-red-600" />
          ) : isActive ? (
            <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
          ) : (
            <FileText className="w-5 h-5 text-gray-400" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900 truncate">{fileItem.name}</span>
            <span className="text-xs text-gray-500 flex-shrink-0">{formatFileSize(fileItem.size)}</span>
          </div>
          {fileItem.message && (
            <p className={`text-xs mt-1 ${
              fileItem.status === 'success' ? 'text-green-600' :
              fileItem.status === 'error' ? 'text-red-600' :
              'text-blue-600'
            }`}>
              {fileItem.message}
            </p>
          )}
          {isActive && (
            <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
              <motion.div
                className="bg-blue-600 h-1.5 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${fileItem.progress}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          )}
        </div>

        <div className="flex-shrink-0 flex items-center gap-1">
          {fileItem.status === 'error' && !isProcessing && (
            <button
              onClick={() => retryFile(fileItem.id)}
              className="p-1.5 text-blue-500 hover:bg-blue-100 rounded-lg transition-colors"
              title="重新上传"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          )}
          {!isProcessing && !isDone && (
            <button
              onClick={() => removeFile(fileItem.id)}
              className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              title="移除文件"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </motion.div>
    );
  };

  const renderStep2 = () => (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl mx-auto">
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
            <Upload className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">步骤 2：上传试卷文件</h3>
            <p className="text-sm text-gray-500">支持 .docx 和 .pdf 格式，最多同时上传 {MAX_FILES} 份试卷</p>
          </div>
        </div>

        <div className="mb-6 p-4 bg-gray-50 rounded-xl">
          <div className="text-sm text-gray-600 mb-2">配置摘要</div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">{inferredConfig.name}</span>
            <span className="text-sm text-gray-500">共 {totalQuestions} 道题</span>
          </div>
        </div>

        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">
              已选择 <span className="text-blue-600 font-bold">{files.length}</span>/{MAX_FILES} 个文件
            </span>
            {remainingSlots > 0 && (
              <span className="text-xs text-gray-500">还可添加 {remainingSlots} 个</span>
            )}
          </div>
          {files.length > 0 && !isProcessing && (
            <button
              onClick={clearAllFiles}
              className="text-xs text-red-500 hover:text-red-600 flex items-center gap-1 transition-colors"
            >
              <Trash2 className="w-3 h-3" />
              清空全部
            </button>
          )}
        </div>

        {files.length > 0 && (
          <div className="mb-4">
            {stats.total > 0 && stats.processing > 0 && (
              <div className="mb-3 p-3 bg-blue-50 rounded-xl flex items-center gap-3">
                <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                <span className="text-sm text-blue-700">
                  正在处理 {stats.processing} 份试卷...
                </span>
              </div>
            )}
            {allDone && (
              <div className="mb-3 p-3 rounded-xl flex items-center gap-3">
                {stats.error === 0 ? (
                  <div className="bg-green-50 rounded-xl p-3 flex items-center gap-3 w-full">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-sm text-green-700">
                      全部完成！成功 {stats.success} 份试卷
                    </span>
                  </div>
                ) : (
                  <div className="bg-amber-50 rounded-xl p-3 flex items-center gap-3 w-full">
                    <AlertCircle className="w-5 h-5 text-amber-600" />
                    <span className="text-sm text-amber-700">
                      处理完成：成功 {stats.success} 份，失败 {stats.error} 份
                    </span>
                  </div>
                )}
              </div>
            )}
            <AnimatePresence>
              {files.map(renderFileItem)}
            </AnimatePresence>
          </div>
        )}

        <div
          className={`border-2 border-dashed rounded-2xl p-8 text-center transition-all ${
            isProcessing ? 'border-gray-200 bg-gray-50 opacity-60' :
            remainingSlots <= 0 ? 'border-gray-200 bg-gray-50' :
            'border-gray-300 hover:border-blue-400 cursor-pointer'
          }`}
          onDragOver={(e) => { if (!isProcessing) e.preventDefault(); }}
          onDrop={isProcessing ? undefined : handleDrop}
        >
          {remainingSlots <= 0 && !isProcessing ? (
            <div className="text-gray-400">
              <FileText className="w-10 h-10 mx-auto mb-3" />
              <p className="text-sm">已达到最大文件数量 ({MAX_FILES})</p>
            </div>
          ) : (
            <>
              <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 mb-2">
                {isProcessing ? '上传中，请等待...' : '拖拽文件到此处，或点击选择'}
              </p>
              <p className="text-xs text-gray-400 mb-4">支持多选，每个文件不超过 10MB</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".docx,.pdf"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                id="file-input"
                disabled={isProcessing || remainingSlots <= 0}
              />
              {!isProcessing && remainingSlots > 0 && (
                <label
                  htmlFor="file-input"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors cursor-pointer"
                >
                  <FileUp className="w-5 h-5" />
                  选择文件
                </label>
              )}
            </>
          )}
        </div>

        {allDone && stats.success > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 flex justify-center"
          >
            <button
              onClick={() => navigate('/admin/pending-papers')}
              className="px-6 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              <ListChecks className="w-5 h-5" />
              查看草稿列表
            </button>
          </motion.div>
        )}

        <div className="mt-6 flex justify-between">
          <button
            onClick={handlePrev}
            disabled={isProcessing}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-5 h-5" />
            上一步
          </button>
          {!allDone && (
            <button
              onClick={processAllFiles}
              disabled={files.length === 0 || isProcessing || files.every(f => f.status === 'success')}
              className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  处理中...
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5" />
                  开始解析 ({files.filter(f => f.status === 'pending' || f.status === 'error').length} 份)
                </>
              )}
            </button>
          )}
          {allDone && (
            <button
              onClick={resetUpload}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors flex items-center gap-2"
            >
              继续上传
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">试卷上传</h1>
        <p className="text-gray-500 mt-1">两步完成试卷上传：配置试卷 → 上传文件（最多 {MAX_FILES} 份）</p>
      </div>

      <div className="mb-8">
        <div className="flex items-center justify-center gap-4">
          {[1, 2].map((step) => (
            <div key={step} className="flex items-center gap-4">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                  currentStep === step
                    ? 'bg-blue-600 text-white'
                    : currentStep > step
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                {currentStep > step ? <CheckCircle className="w-5 h-5" /> : step}
              </div>
              {step < 2 && (
                <div
                  className={`w-16 h-1 rounded ${
                    currentStep > step ? 'bg-green-500' : 'bg-gray-200'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
        <div className="flex items-center justify-center gap-24 mt-2 text-sm text-gray-500">
          <span>配置试卷</span>
          <span>上传文件</span>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
      </AnimatePresence>
    </div>
  );
}